import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AlertCircle, CheckCircle2, ExternalLink, Lightbulb } from 'lucide-react';

import { useAppSelector } from '../../../hooks';
import { getNorms } from '../../../store/selectors/user.selector';
import { MarketMatchResultsPreviewModal } from '../sidebar/MarketMatchResultsPreviewModal.tsx';
import { formatLumen, getLumenDeltaColor, useAppliedMatchSummary } from '../useAppliedMarketMatch.ts';

function LumenBar({
  label,
  value,
  max,
  barClass,
  delta,
}: {
  label: string;
  value: number | null;
  max: number;
  barClass: string;
  delta?: number | null;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-[72px_minmax(0,1fr)_auto] items-center gap-2 text-[11px]">
      <span className="truncate text-gray-400">{label}</span>
      <div className="h-1.5 overflow-hidden rounded-full bg-white/[0.07]">
        <div
          className={`h-full rounded-full ${barClass}`}
          style={{ width: value ? `${Math.max(3, (value / max) * 100)}%` : 0 }}
        />
      </div>
      <span className="text-right tabular-nums text-gray-100">
        {value != null ? formatLumen(value) : t('common.n_a')}
        {delta != null && (
          <span className={`ml-1 ${getLumenDeltaColor(delta)}`}>
            {delta > 0 ? '+' : ''}
            {delta.toFixed(0)}%
          </span>
        )}
      </span>
    </div>
  );
}

/** The applied market match in the side panel's footer: which product, whether it meets the
 *  targets, and how its total lumen output compares with the optimal configuration. */
export function AppliedMatchCard() {
  const { t } = useTranslation();
  const summary = useAppliedMatchSummary();
  const norms = useAppSelector(getNorms);
  const [resultsOpen, setResultsOpen] = useState(false);
  if (!summary) return null;

  const { detail, productName, placementCount, catalogueUrl, targets, allTargetsSatisfied } = summary;
  const { appliedTotalLumen, idealTotalLumen, lumenDeltaPct } = summary;
  const maxLumen = Math.max(appliedTotalLumen ?? 0, idealTotalLumen ?? 0);
  const matchBar =
    lumenDeltaPct != null && lumenDeltaPct > 0.5
      ? 'bg-amber-400'
      : lumenDeltaPct != null
        ? 'bg-lime-400'
        : 'bg-white/40';

  return (
    <>
      <div className="space-y-3 rounded-lg bg-white/[0.04] p-3">
        <div className="flex gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-md border border-white/10 bg-black/40 p-0.5">
            {detail.imageUrl ? (
              <img src={detail.imageUrl} alt={productName} className="max-h-full max-w-full object-contain" />
            ) : (
              <Lightbulb size={18} className="text-gray-500" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[10px] font-semibold uppercase tracking-wider text-lime-400">
              {t('workspace.panel.applied.label')}
              {detail.manufacturerName && (
                <span className="font-normal normal-case tracking-normal text-gray-400">
                  {' '}
                  · {detail.manufacturerName}
                </span>
              )}
            </p>
            <p className="mt-0.5 truncate text-[14px] font-semibold text-white" title={productName}>
              {productName}
            </p>
            <p className="mt-0.5 flex items-center gap-2 text-[11px] text-gray-400">
              {placementCount > 0 && <span>{t('workspace.placements', { count: placementCount })}</span>}
              {catalogueUrl && (
                <a
                  href={catalogueUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-0.5 text-lime-400 hover:underline"
                >
                  {t('workspace.panel.applied.catalogue')}
                  <ExternalLink size={10} />
                </a>
              )}
            </p>
          </div>
        </div>

        {targets.total > 0 && (
          <div className="flex items-center justify-between gap-2 text-[12px]">
            <span
              className={`flex items-center gap-1.5 font-medium ${allTargetsSatisfied ? 'text-lime-300' : 'text-red-300'}`}
            >
              {allTargetsSatisfied ? (
                <CheckCircle2 size={14} className="text-lime-400" />
              ) : (
                <AlertCircle size={14} className="text-red-400" />
              )}
              {t('workspace.targetsSatisfied', targets)}
            </span>
            {summary.aggregateResults.length > 0 && (
              <button
                type="button"
                onClick={() => setResultsOpen(true)}
                className="shrink-0 font-medium text-white underline decoration-white/40 underline-offset-2 hover:decoration-white"
              >
                {t('workspace.panel.applied.viewResults')}
              </button>
            )}
          </div>
        )}

        {maxLumen > 0 && (
          <div className="space-y-1.5">
            <LumenBar
              label={t('workspace.panel.applied.thisMatch')}
              value={appliedTotalLumen}
              max={maxLumen}
              barClass={matchBar}
              delta={lumenDeltaPct}
            />
            <LumenBar
              label={t('workspace.appliedConfigurationIdealLumen')}
              value={idealTotalLumen}
              max={maxLumen}
              barClass="bg-white/35"
            />
          </div>
        )}
      </div>

      <MarketMatchResultsPreviewModal
        isOpen={resultsOpen}
        lampName={productName}
        aggregateResults={summary.aggregateResults}
        layerDisplayNames={summary.layerDisplayNames}
        norms={norms}
        onClose={() => setResultsOpen(false)}
      />
    </>
  );
}
